import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { KeyValue } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { IClient } from '@techspert-io/clients';
import {
  ConnectPhase,
  ExpertValidationService,
  IDisplayExpert,
  IExpert,
  IExpertUpdateRequest,
  connectPhaseList,
  getExpertRejectionMock,
} from '@techspert-io/experts';
import { IOpportunity, PortalLinksPipe } from '@techspert-io/opportunities';
import {
  IExpertScreenerAction,
  ScreenerResultDialogComponent,
} from '@techspert-io/surveys';
import { ToastService } from '@techspert-io/user-alerts';
import { Subject, combineLatest, fromEvent, of } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
} from '../../../../shared/patterns/confirmation-dialog/confirmation-dialog.component';
import { FileService } from '../../../../shared/services/file.service';
import {
  IOpportunitySearchSelectDialogData,
  OpportunitySearchSelectDialogComponent,
} from './opportunity-search-select-dialog/opportunity-search-select-dialog.component';
import {
  IRespondedDialogInput,
  IRespondedDialogResponse,
  RespondedDialogComponent,
} from './responded-dialog/responded-dialog.component';
import {
  ISendSolicitationDialogData,
  SendSolicitationDialogComponent,
} from './send-solicitation-dialog/send-solicitation-dialog.component';
import { OpportunityExpertsService } from './services/opportunity-experts.service';

type OpportunityPhases = ConnectPhase | 'rejected';

interface ISearchConfig {
  batchId: string;
  sourceIds: string[];
}

interface ISegmentFilterOption {
  id: string;
  label: string;
  count: number;
}

@Component({
  selector: 'app-opportunity-results',
  templateUrl: './opportunity-results.component.html',
  styleUrls: ['./opportunity-results.component.scss'],
  providers: [PortalLinksPipe],
  standalone: false,
})
export class OpportunityResultsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() client: IClient;
  @Input() opportunity: IOpportunity;
  @Input() selectedPhase: OpportunityPhases = 'identified';

  @ViewChild('phaseMenu')
  phaseMenu: TemplateRef<unknown>;

  private destroy$ = new Subject();

  selectedSearch: string = 'all-searches';
  expertsTotal: number;
  overlayRef: OverlayRef | null;
  experts: IDisplayExpert[] = [];
  filteredExperts: IDisplayExpert[] = [];
  searchCtrl = new FormControl('', { nonNullable: true });
  selectAllToggle = false;
  selectedExpertsCount = 0;
  filterEmails = true;
  filterAutoExperts = true;
  openFilter = false;
  showAffiliationsToggle = false;
  isDownloadingSearches = true;
  isDownloadingExperts = false;

  selectedSearches: ISearchConfig[] = [];

  segmentOptions: ISegmentFilterOption[] = [];
  selectedSegmentIds: string[] = [];

  overriddenSelectedSearches: ISearchConfig[];
  eligiblePhaseMenuOptions: Array<KeyValue<OpportunityPhases, string>> = [];

  get hasAutomatedExperts(): boolean {
    return this.experts.some((d) => !!d.campaignId);
  }

  private static readonly PHASES: Map<OpportunityPhases, string> = new Map([
    ['identified', 'Identified'],
    ['firstFollowUp', 'Follow up 1'],
    ['secondFollowUp', 'Follow up 2'],
    ['outreachComplete', 'Outreach Complete'],
    ['outreach', 'Responded'],
    ['screener', 'Screener'],
    ['sentToClient', 'In Portal'],
    ['accepted', 'Accepted'],
    ['scheduled', 'Scheduled'],
    ['completed', 'Completed'],
    ['rejected', 'Reject'],
  ]);
  static readonly NEXT = 'next';
  static readonly PREVIOUS = 'previous';

  constructor(
    private router: Router,
    private title: Title,
    private dialog: MatDialog,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private opportunityExpertsService: OpportunityExpertsService,
    private expertValidationService: ExpertValidationService,
    private toastService: ToastService,
    private fileService: FileService
  ) {}

  get isLoading() {
    return this.isDownloadingSearches || this.isDownloadingExperts;
  }

  ngOnInit() {
    this.initialiseData(this.opportunity);

    combineLatest([
      this.opportunityExpertsService.experts$,
      this.opportunityExpertsService.sources$,
      this.opportunityExpertsService.downloadedSearches$,
    ])
      .pipe(
        tap(([experts, sources, downloadedSearches]) => {
          this.isDownloadingExperts = Object.values(downloadedSearches).some(
            (d) => ['init', 'loading'].includes(d)
          );

          const mappedSources = [...new Set(sources.map((s) => s.batchId))].map(
            (batchId) => ({
              batchId,
              sourceIds: sources
                .filter((s) => s.batchId === batchId)
                .map((s) => s.expertSourceId),
            })
          );

          this.experts = experts;
          this.selectedSearches =
            this.overriddenSelectedSearches || mappedSources;

          this.buildSegmentOptions();
          this.applyFilter();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.opportunityExpertsService.expertTotals$
      .pipe(
        tap(({ isLoading, batches }) => {
          this.isDownloadingSearches = isLoading;

          this.expertsTotal = Object.values(batches).reduce(
            (prev, val) =>
              prev +
              Object.values(val.stats).reduce((acc, curr) => acc + curr, 0),
            this.experts.filter((d) => Object.values(d.rejection).some(Boolean))
              .length
          );
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.opportunity &&
      !changes.opportunity.firstChange &&
      changes.opportunity.currentValue.opportunityId !==
        changes.opportunity.previousValue.opportunityId
    ) {
      this.searchCtrl.setValue('');
      this.selectedExpertsCount = 0;
      this.selectAllToggle = false;
      this.filterEmails = true;
      this.filterAutoExperts = true;
      this.segmentOptions = [];
      this.selectedSegmentIds = [];
      this.overriddenSelectedSearches = undefined;
      this.isDownloadingSearches = true;
      this.isDownloadingExperts = true;
      this.initialiseData(changes.opportunity.currentValue);
    }
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.title.setTitle('CONNECT');
  }

  addExpertiseToSearch(expertise: string) {
    this.searchCtrl.setValue(expertise);
    this.applyFilter();
  }

  navigateToExpert(expert: IExpert) {
    this.selectPhase(
      Object.values(expert.rejection).some(Boolean) || expert.blocked
        ? 'rejected'
        : expert.connectPhase
    );
  }

  rejectExperts() {
    const experts = this.experts
      .filter((expert) => expert.isSelected)
      .map((expert) => ({
        rejection: {
          ...expert.rejection,
          admin: getExpertRejectionMock(Date.now()),
        },
        expertId: expert.expertId,
      }));

    this.updateExperts(experts).subscribe();
  }

  unrejectExperts() {
    const updatedExperts = this.experts.filter((expert) => expert.isSelected);

    const nonAdminRejected = updatedExperts.filter((e) =>
      Object.entries(e.rejection).some(([type, r]) => r && type !== 'admin')
    );

    const expertUpdates = updatedExperts.map(({ expertId }) => ({
      expertId,
      rejection: {},
    }));

    if (nonAdminRejected.length) {
      const expertText = nonAdminRejected.length > 1 ? 'experts' : 'expert';

      this.dialog
        .open<ConfirmationDialogComponent, IConfirmationDialogData, boolean>(
          ConfirmationDialogComponent,
          {
            width: '400px',
            height: '150px',
            data: {
              messages: [
                `This will overwrite the expert's and/or client's rejection for ${nonAdminRejected.length} ${expertText}. Are you sure?`,
              ],
              positiveButtonText: 'Yes',
              negativeButtonText: 'No',
              isDangerous: true,
            },
          }
        )
        .afterClosed()
        .pipe(
          takeWhile((result) => result),
          switchMap(() => this.updateExperts(expertUpdates))
        )
        .subscribe();
    } else {
      this.updateExperts(expertUpdates).subscribe();
    }
  }

  toggleAdminRejectedExpertStatus(expert: IExpert) {
    if (Object.values(expert.rejection).every((r) => !r)) {
      this.updateExperts([
        {
          expertId: expert.expertId,
          rejection: {
            ...expert.rejection,
            admin: getExpertRejectionMock(Date.now()),
          },
        },
      ]).subscribe();
    } else if (
      Object.entries(expert.rejection).some(
        ([type, r]) => r && type !== 'admin'
      )
    ) {
      this.dialog
        .open<ConfirmationDialogComponent, IConfirmationDialogData, boolean>(
          ConfirmationDialogComponent,
          {
            width: '400px',
            minHeight: '150px',
            data: {
              messages: [
                "This will overwrite the expert's and/or client's rejection for the target expert. Are you sure?",
              ],
              positiveButtonText: 'Yes',
              negativeButtonText: 'No',
              isDangerous: true,
            },
          }
        )
        .afterClosed()
        .pipe(
          takeWhile((result) => result),
          switchMap(() =>
            this.updateExperts([
              {
                expertId: expert.expertId,
                rejection: {},
                reasonForRejection: 'NULL',
              },
            ])
          )
        )
        .subscribe();
    } else if (expert.rejection.admin) {
      this.updateExperts([
        {
          expertId: expert.expertId,
          rejection: {},
          reasonForRejection: 'NULL',
        },
      ]).subscribe();
    }
  }

  selectPhase(event: OpportunityPhases) {
    this.selectedExpertsCount = 0;
    this.selectAllToggle = false;
    this.selectedPhase = event;

    this.opportunityExpertsService.setSelected(
      this.experts.map((expert) => ({
        expertId: expert.expertId,
        isSelected: false,
      }))
    );

    this.router.navigate([
      'admin',
      'client',
      this.client.clientId,
      'opportunity',
      this.opportunity.opportunityId,
      'search',
      this.selectedSearch,
      'phase',
      this.selectedPhase,
    ]);
  }

  selectExpert(selectedExpert: IDisplayExpert) {
    this.opportunityExpertsService.setSelected([
      {
        expertId: selectedExpert.expertId,
        isSelected: !selectedExpert.isSelected,
      },
    ]);
  }

  selectAllExperts() {
    this.selectAllToggle = !this.selectAllToggle;
    this.setExpertSelectedStatus(this.selectAllToggle);
  }

  applyFilterEmails() {
    this.filterEmails = !this.filterEmails;
    this.applyFilter();
  }

  applyFilterAutoExperts() {
    this.filterAutoExperts = !this.filterAutoExperts;
    this.applyFilter();
  }

  toggleSegmentFilter(segmentId: string) {
    this.selectedSegmentIds = this.selectedSegmentIds.includes(segmentId)
      ? this.selectedSegmentIds.filter((id) => id !== segmentId)
      : [...this.selectedSegmentIds, segmentId];
    this.applyFilter();
  }

  onSearchTermChange(searchTerm: string) {
    this.searchCtrl.setValue(searchTerm);
    this.applyFilter();
  }

  applyFilter() {
    this.filteredExperts = this.experts.filter(
      (expert) =>
        this.searchFilter(expert) &&
        (expert.connectPhase === this.selectedPhase ||
          this.selectedPhase === 'rejected') &&
        (this.selectedPhaseNotRejected(expert) ||
          this.selectedPhaseRejected(expert)) &&
        (expert.primaryEmail ||
          expert.opportunityEmails.length ||
          !this.filterEmails) &&
        (!this.searchCtrl.value || this.checkFields(expert)) &&
        (expert.isSelectable || !this.filterAutoExperts) &&
        this.segmentFilter(expert)
    );

    this.openFilter = false;
    if (!this.filteredExperts.length && this.searchCtrl.value) {
      this.openFilter = true;
      this.filteredExperts = this.experts.filter(
        (expert) => !this.searchCtrl.value || this.checkFields(expert)
      );
    }
    this.updateSelectedCount();
  }

  openEmailDialog() {
    if (this.selectedPhase === 'rejected') return;

    const reachableExperts = this.experts.filter(
      (expert) =>
        expert.isSelected &&
        (expert.primaryEmail || expert.opportunityEmails.length)
    );

    const reachableExpertMap = reachableExperts.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.expertId]: curr,
      }),
      {}
    );

    const experts = this.experts.map((expert) => ({
      ...expert,
      isSelected: !!reachableExpertMap[expert.expertId],
    }));
    this.opportunityExpertsService.setSelected(experts);

    this.dialog
      .open<
        SendSolicitationDialogComponent,
        ISendSolicitationDialogData,
        undefined
      >(SendSolicitationDialogComponent, {
        width: '1600px',
        height: '80%',
        data: {
          experts: reachableExperts,
          opportunity: this.opportunity,
          activePhase: this.selectedPhase,
          allExperts: experts,
        },
      })
      .afterClosed()
      .subscribe();
  }

  downloadClerkChatList() {
    const selected = this.experts.filter((e) => e.isSelected);
    const eligible = selected.filter(
      (e) => e.smsProjectInvitesOptIn && !!e.primaryPhoneNumber
    );

    if (!eligible.length) {
      this.toastService.sendMessage(
        'None of the selected experts are eligible for SMS export (must have opted in to SMS contact and have a phone number on file).',
        'danger'
      );
      return;
    }

    if (eligible.length < selected.length) {
      this.toastService.sendMessage(
        `${selected.length - eligible.length} of ${selected.length} selected experts were excluded (not opted in to SMS contact or missing a phone number).`,
        'info'
      );
    }

    this.fileService.exportToCsv(
      eligible.map((e) => this.expertToClerkChatCsvRow(e)),
      { filename: 'clerk-chat-list', bom: true }
    );
  }

  openRespondedDialog(targetPhase: ConnectPhase) {
    this.dialog
      .open<
        RespondedDialogComponent,
        IRespondedDialogInput,
        IRespondedDialogResponse
      >(RespondedDialogComponent, {
        width: '750px',
        height: '80%',
        data: {
          experts: this.experts.filter((expert) => expert.isSelected),
          opportunity: this.opportunity,
          targetPhase: targetPhase,
        },
      })
      .afterClosed()
      .pipe(
        filter((experts) => !!experts && !!experts.length),
        switchMap((experts) => this.updateExperts(experts))
      )
      .subscribe();
  }

  getPhase(currentPhase: OpportunityPhases, direction: string) {
    const phases = Array.from(OpportunityResultsComponent.PHASES.keys());
    switch (direction) {
      case OpportunityResultsComponent.PREVIOUS:
        return phases[phases.indexOf(currentPhase) - 1] || 'identified';
      case OpportunityResultsComponent.NEXT:
        if (currentPhase === 'completed') return currentPhase;
        // TODO: Below will never return rejected
        return phases[phases.indexOf(currentPhase) + 1] || 'rejected';
    }
  }

  attachPhaseMenuOverlay(event: MouseEvent, action: 'previous' | 'next') {
    this.closePhaseMenuOverlay();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(event)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.overlayRef.attach(
      new TemplatePortal(this.phaseMenu, this.viewContainerRef, {
        action,
      })
    );

    fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter((event) => {
          const clickTarget = event.target as HTMLElement;
          return (
            !!this.overlayRef &&
            !this.overlayRef.overlayElement.contains(clickTarget)
          );
        }),
        take(1)
      )
      .subscribe(() => this.closePhaseMenuOverlay());
  }

  showExpertScreenerResult(expert: IDisplayExpert) {
    if (expert.screenerConfig.dateCompleted) {
      this.dialog
        .open<
          ScreenerResultDialogComponent,
          { expertActionId: string },
          IExpertScreenerAction
        >(ScreenerResultDialogComponent, {
          width: '900px',
          minHeight: '400px',
          data: {
            expertActionId: expert.screenerConfig.expertActionId,
          },
          disableClose: false,
        })
        .afterClosed()
        .pipe(
          takeWhile((screenerConfig) => !!screenerConfig),
          tap((screenerConfig) =>
            this.opportunityExpertsService.patchExpert({
              ...expert,
              screenerConfig,
            })
          )
        )
        .subscribe();
    }
  }

  showSearchesSelect() {
    this.dialog
      .open<
        OpportunitySearchSelectDialogComponent,
        IOpportunitySearchSelectDialogData,
        ISearchConfig[]
      >(OpportunitySearchSelectDialogComponent, {
        width: '900px',
        minHeight: '75%',
        data: {
          searches: this.selectedSearches,
          opportunityId: this.opportunity.opportunityId,
        },
      })
      .afterClosed()
      .pipe(
        takeWhile((res) => !!res),
        map((res) => {
          this.selectedSearches = res;
          this.overriddenSelectedSearches = res;
          this.applyFilter();
        })
      )
      .subscribe();
  }

  closePhaseMenuOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  forwardRightClick(event: MouseEvent) {
    this.eligiblePhaseMenuOptions = this.getPhasesBeforeOrAfter(
      this.selectedPhase,
      false
    );
    this.attachPhaseMenuOverlay(event, 'next');
  }

  backwardRightClick(event: MouseEvent) {
    this.eligiblePhaseMenuOptions = this.getPhasesBeforeOrAfter(
      this.selectedPhase,
      true
    );
    this.attachPhaseMenuOverlay(event, 'previous');
  }

  isBackButtonDisabled() {
    return (
      !this.selectedExpertsCount ||
      (this.selectedExpertsCount > 1 &&
        (this.selectedPhase === 'completed' ||
          this.selectedPhase === 'scheduled'))
    );
  }

  handlePhaseMenuSelect(
    action: 'previous' | 'next',
    phase?: OpportunityPhases
  ) {
    this.moveExperts(
      phase || this.getPhase(this.selectedPhase, action),
      action
    );
  }

  private expertToClerkChatCsvRow(expert: IDisplayExpert) {
    return {
      'First name': expert.firstName ?? '',
      'Last name': expert.lastName ?? '',
      'Phone number': expert.primaryPhoneNumber ?? '',
    };
  }

  private updateSelectedCount() {
    this.selectedExpertsCount = this.experts.filter(
      (expert) => expert.isSelected
    ).length;
  }

  private checkFields(expert: IDisplayExpert) {
    return (
      this.checkName(expert) ||
      this.checkLastName(expert) ||
      this.checkEmail(expert) ||
      this.checkScreener(expert)
    );
  }

  private checkEmail(expert: IExpert) {
    return (expert.opportunityEmails || []).some((email) =>
      email.toLowerCase().includes(this.searchCtrl.value.toLowerCase())
    );
  }

  private checkName(expert: IExpert) {
    const expertFullName = `${expert.firstName || ''} ${expert.lastName || ''}`;
    return (
      expertFullName &&
      expertFullName.toLowerCase().includes(this.searchCtrl.value.toLowerCase())
    );
  }

  private checkLastName(expert: IExpert) {
    return (
      expert.lastName &&
      expert.lastName
        .toLowerCase()
        .includes(this.searchCtrl.value.toLowerCase())
    );
  }

  private checkScreener(expert: IDisplayExpert) {
    return (
      expert.screenerConfig?.expertActionId &&
      expert.screenerConfig?.expertActionId ===
        this.searchCtrl.value.toLowerCase()
    );
  }

  private selectedPhaseNotRejected(expert: IExpert) {
    return (
      this.selectedPhase !== 'rejected' &&
      Object.values(expert.rejection).every((r) => !r)
    );
  }

  private selectedPhaseRejected(expert: IExpert) {
    return (
      this.selectedPhase === 'rejected' &&
      Object.values(expert.rejection).some(Boolean)
    );
  }

  private searchFilter(expert: IExpert) {
    const selectedSearchSourceIds = this.selectedSearches.flatMap(
      (s) => s.sourceIds
    );
    return selectedSearchSourceIds.includes(expert.expertSourceId);
  }

  private segmentFilter(expert: IExpert) {
    return (
      !this.selectedSegmentIds.length ||
      this.selectedSegmentIds.includes(expert.opportunitySegmentId)
    );
  }

  private buildSegmentOptions() {
    const counts = this.experts.reduce(
      (map, expert) =>
        expert.opportunitySegmentId
          ? map.set(
              expert.opportunitySegmentId,
              (map.get(expert.opportunitySegmentId) ?? 0) + 1
            )
          : map,
      new Map<string, number>()
    );

    this.segmentOptions = [...counts.entries()]
      .map(([id, count]) => {
        const segment = (this.opportunity?.segments || {})[id];
        return {
          id,
          label: segment ? `${segment.geography} · ${segment.segment}` : id,
          count,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    this.selectedSegmentIds = this.selectedSegmentIds.filter((id) =>
      counts.has(id)
    );
  }

  private getPhasesBeforeOrAfter(
    currentPhase: OpportunityPhases,
    fetchPreceding: boolean
  ) {
    const phases = Array.from(OpportunityResultsComponent.PHASES.keys());

    const currentPhaseIndex = phases.indexOf(currentPhase);
    const outReachPhaseIndex = phases.indexOf('outreach');

    let start = 0;
    let end = currentPhaseIndex - 1;

    if (!fetchPreceding) {
      start = currentPhaseIndex + 1;
      end = phases.length - 1;
    } else if (currentPhaseIndex >= outReachPhaseIndex) {
      start = outReachPhaseIndex;
    }

    const result: Array<KeyValue<OpportunityPhases, string>> = [];
    while (start <= end) {
      result.push({
        key: phases[start],
        value: OpportunityResultsComponent.PHASES.get(phases[start]),
      });
      start++;
    }

    if (fetchPreceding) {
      return result.reverse();
    } else {
      return result;
    }
  }

  private moveExperts(targetPhase: OpportunityPhases, action: string) {
    this.closePhaseMenuOverlay();

    if (!this.experts.some((e) => e.isSelected)) {
      return;
    }

    if (targetPhase === 'rejected') {
      this.rejectExperts();
      return;
    }

    if (action === OpportunityResultsComponent.PREVIOUS) {
      return this.updatePhase(targetPhase);
    }

    this.expertValidationService
      .isValid(
        this.experts.filter((e) => e.isSelected),
        targetPhase,
        this.opportunity.isAllowedHighRiskExpertsInClientPortal
      )
      .pipe(
        map(({ valid, reason }) => {
          if (valid) {
            return this.updatePhase(targetPhase);
          }

          if (!reason) {
            return this.openRespondedDialog(targetPhase);
          }

          this.toastService.sendMessage(reason, 'danger');
        })
      )
      .subscribe();
  }

  private updatePhase(targetPhase: ConnectPhase) {
    this.updateExperts(
      this.experts
        .filter((e) => e.isSelected)
        .map((e) => ({
          expertId: e.expertId,
          connectPhase: targetPhase,
        }))
    ).subscribe();
  }

  private setExpertSelectedStatus(toggle: boolean) {
    const filteredExpertIds = new Set(
      this.filteredExperts
        .filter(
          (e) => connectPhaseList.indexOf(e.connectPhase) > 2 || !e.campaignId
        )
        .map((e) => e.expertId)
    );

    this.opportunityExpertsService.setSelected(
      this.experts.map((e) => ({
        expertId: e.expertId,
        isSelected: filteredExpertIds.has(e.expertId) ? toggle : e.isSelected,
      }))
    );
  }

  private updateExperts(
    experts: IExpertUpdateRequest[],
    resetIsSelected: boolean = true
  ) {
    this.opportunityExpertsService.updateMany(experts, resetIsSelected);

    return of(experts as IExpert[]);
  }

  private initialiseData(opportunity: IOpportunity) {
    this.title.setTitle(opportunity.opportunityName);
  }
}
